// Orchestrates the auto-rig feature end to end (docs/3d-content-pipeline/
// roadmap.md 3.3): turns an uploaded static mesh plus a set of landmark
// markers into a real, exportable THREE.SkinnedMesh bound to the canonical
// seven-bone skeleton (`AUTO_RIG_BONE_NAMES`), by combining
// `inferCanonicalJoints`/`toInfluenceSegments` (autoRigJoints.ts) with
// `buildMeshGraph`/`computeGeodesicSkinWeights` (autoRigGeometry.ts).
//
// Because the produced skeleton's bone names are exactly `CORE_RIG_BONES`
// (see autoRigJoints.ts's own doc comment), the result plays every canonical
// pose/clip as the identity mapping once exported and re-imported through
// the existing `createImportedSkeletonRenderer` path - no bone-mapping step,
// unlike the general import-and-map flow in importRig.ts/screens-editor.tsx.
import {
  Bone, BufferGeometry, Float32BufferAttribute, Group, Material, MeshStandardMaterial,
  Skeleton, SkinnedMesh, Uint16BufferAttribute,
} from 'three';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

import { buildMeshGraph, computeGeodesicSkinWeights } from './autoRigGeometry';
import { AUTO_RIG_BONE_NAMES, AUTO_RIG_HIERARCHY, inferCanonicalJoints, toInfluenceSegments, type LandmarkMarkers } from './autoRigJoints';

const DEFAULT_MATERIAL = (): Material => new MeshStandardMaterial({ color: '#9aa6b4', roughness: 0.72, metalness: 0.05 });

/**
 * Welds an uploaded mesh's geometry into a connectivity-complete form before
 * the geodesic search runs. Always runs `mergeVertices` - even on an
 * already-indexed upload - rather than only on a raw (non-indexed) one:
 * glTF exports are normally indexed already, but routinely still carry
 * duplicate co-located vertices at UV/normal/smoothing seams (exactly at
 * armpits, wrists, the neck, the crotch - precisely where markers get
 * placed), which an "already indexed" check alone would miss entirely. A
 * seam where UVs or normals genuinely differ stays split after welding (its
 * own honest limit, same spirit as this module's other documented v1
 * constraints), but ordinary shared edges weld back into one connected
 * surface either way. Always returns a fresh clone (three.js's own
 * `mergeVertices` never mutates its input), so this never mutates the
 * caller's own geometry.
 */
function weldForSkinning(geometry: BufferGeometry): BufferGeometry {
  return mergeVertices(geometry);
}

/**
 * Builds the seven-bone canonical skeleton's rest pose (`AUTO_RIG_HIERARCHY`
 * parenting, each joint's own `localPosition`) as a real `THREE.Bone` tree,
 * rooted at `hips`.
 */
function buildBoneHierarchy(joints: ReturnType<typeof inferCanonicalJoints>): { root: Bone; bones: Bone[] } {
  const byName = new Map<string, Bone>();
  for (const name of AUTO_RIG_BONE_NAMES) {
    const bone = new Bone();
    bone.name = name;
    byName.set(name, bone);
  }
  const jointByName = new Map(joints.map((j) => [j.name, j] as const));
  for (const name of AUTO_RIG_BONE_NAMES) {
    const bone = byName.get(name)!;
    const joint = jointByName.get(name);
    if (joint) bone.position.set(...joint.localPosition);
    const parentName = AUTO_RIG_HIERARCHY[name];
    if (parentName) byName.get(parentName)!.add(bone);
  }
  const root = byName.get('hips')!;
  const bones = AUTO_RIG_BONE_NAMES.map((name) => byName.get(name)!);
  return { root, bones };
}

/**
 * The full auto-rig build: welds `sourceGeometry`, computes geodesic skin
 * weights from `markers`, constructs the canonical bone hierarchy at the
 * inferred rest pose, and binds a `THREE.SkinnedMesh` to it. The returned
 * `scene` is a self-contained `Group` (mesh + its own skeleton root as a
 * child) ready to hand to `GLTFExporter`, the same export path
 * `modelGlbExport.ts` already uses for Model Studio.
 *
 * Async because `computeGeodesicSkinWeights` is: it yields back to the
 * browser periodically rather than running the whole geodesic search
 * synchronously in one call, so a "Build rig" click stays responsive on a
 * thousands-of-vertices upload instead of freezing the tab.
 */
export async function buildAutoRig(sourceGeometry: BufferGeometry, markers: LandmarkMarkers, material: Material = DEFAULT_MATERIAL()): Promise<{ scene: Group; mesh: SkinnedMesh }> {
  const geometry = weldForSkinning(sourceGeometry);
  const positionAttr = geometry.attributes.position;
  if (!positionAttr) throw new Error('buildAutoRig: geometry has no position attribute.');
  const positions = positionAttr.array instanceof Float32Array ? positionAttr.array : new Float32Array(positionAttr.array);
  // weldForSkinning always runs mergeVertices, which always sets an index -
  // geometry.index is guaranteed non-null here.
  const indices = geometry.index!.array;

  const joints = inferCanonicalJoints(markers);
  const graph = buildMeshGraph(positions, indices);
  const { skinIndices, skinWeights } = await computeGeodesicSkinWeights(graph, toInfluenceSegments(joints));

  geometry.setAttribute('skinIndex', new Uint16BufferAttribute(skinIndices, 4));
  geometry.setAttribute('skinWeight', new Float32BufferAttribute(skinWeights, 4));

  const { root, bones } = buildBoneHierarchy(joints);
  const mesh = new SkinnedMesh(geometry, material);
  mesh.add(root);
  mesh.updateMatrixWorld(true);
  const skeleton = new Skeleton(bones);
  mesh.bind(skeleton, mesh.matrixWorld);

  const scene = new Group();
  scene.add(mesh);
  return { scene, mesh };
}
